import { decodeErrorResult, parseAbi } from "viem";
import { ponsRewardNftAbi } from "./pons-reward-nft";

const revertAbi = [...ponsRewardNftAbi, ...parseAbi([
  "error BlacklistedAddress(address wallet)",
  "error ReferralAlreadyClaimed()",
  "error QuiverNotReady(uint8 status,uint256 liveFeeWei,uint256 budgetWei)",
  "error InsufficientTokenPool(address sourceToken,uint256 availableWei,uint256 requestedWei)",
  "error EtherTransferFailed()",
  "error AccountingInvariantBroken(uint256 allocatedWei,uint256 backingWei)",
  "error ERC721NonexistentToken(uint256 tokenId)",
  "error ECDSAInvalidSignature()",
  "error ECDSAInvalidSignatureLength(uint256 length)",
  "error ECDSAInvalidSignatureS(bytes32 s)",
  "error ReentrancyGuardReentrantCall()",
])];

const messages: Record<string, string> = {
  BlacklistedAddress: "This wallet or route is blocked by the token contract.",
  ReferralAlreadyClaimed: "This wallet has already claimed this referral milestone NFT.",
  TaskMintAlreadyClaimed: "This wallet has already claimed its social task NFT.",
  TaskMintCapReached: "All social task NFTs have been claimed.",
  AuthorizationWalletMismatch: "Connect the wallet named in the authorization.",
  AuthorizationExpired: "The transaction deadline has expired. Prepare and review again.",
  AuthorizationNotYetValid: "The authorization is not valid yet. Check the network time.",
  ProofAlreadyConsumed: "This proof has already been used to mint an NFT.",
  IncorrectMintFee: "The ETH value does not match the current mint fee. Prepare again.",
  StaleQuotedMintFee: "The mint fee changed. Prepare and review a new quote.",
  InvalidAuthorizationSignature: "The backend authorization signature is invalid.",
  RandomnessProviderUnavailable: "The randomness provider is not ready. Try again when readiness recovers.",
  QuiverNotReady: "Quiver is not ready. Check provider status and budget.",
  InvalidRewardStatus: "The NFT is not in the required state. Refresh its details.",
  RewardNotFinalized: "Wait for the reward to be finalized.",
  UnauthorizedRedeemer: "Only the current NFT owner can redeem.",
  PayoutBelowMinimum: "The pool changed beyond the reviewed minimum. Review a fresh preview.",
  PayoutIsZero: "The pool is empty or the payout rounds to zero.",
  ERC721NonexistentToken: "The NFT does not exist or has already been burned.",
  EtherTransferFailed: "The recipient could not receive ETH. The redemption was reverted.",
};

/** Decode structured RPC revert data only; never print payloads or signed calldata. */
export class ContractReadError extends Error {
  constructor(readonly errorName: string, message: string, readonly contractAddress: string | undefined,
    readonly payoutWei?: bigint, readonly minimumPayoutWei?: bigint) {
    super(message);
    this.name = "ContractReadError";
  }
}

/** The caller binds contractAddress only for an exact simulated operation call. */
export function contractReadError(error: unknown, contractAddress?: string): Error {
  const data = revertData(error);
  if (data) {
    try {
      const decoded = decodeErrorResult({ abi: revertAbi, data });
      const [payout, minimum] = decoded.args ?? [];
      const message = decoded.errorName === "PayoutBelowMinimum" && typeof payout === "bigint" && typeof minimum === "bigint" ?
        `The contract reports payout ${payout} wei below minimum ${minimum} wei. Review a fresh preview.` :
        messages[decoded.errorName] ?? "Contract preflight failed. Refresh the transaction details.";
      return new ContractReadError(decoded.errorName,
        `${decoded.errorName}: ${message}`, contractAddress,
        decoded.errorName === "PayoutBelowMinimum" && typeof payout === "bigint" ? payout : undefined,
        decoded.errorName === "PayoutBelowMinimum" && typeof minimum === "bigint" ? minimum : undefined);
    } catch {
      return new Error(`ContractRevert(${data.slice(0, 10)}): Transaction simulation failed.`);
    }
  }
  return new Error("Transaction preflight could not be completed. Check the RPC connection and try again.");
}

function revertData(value: unknown, depth = 0): `0x${string}` | null {
  if (depth > 6) return null;
  if (typeof value === "string") return /^0x(?:[a-f0-9]{2}){4,}$/i.test(value) ? value as `0x${string}` : null;
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  for (const key of ["data", "cause", "error", "originalError"]) {
    const found = revertData(record[key], depth + 1);
    if (found) return found;
  }
  return null;
}
